// Хедърът на прозореца: тих mono ред с идентификацията, едро име на масата,
// един ред за сервитьора и състоянието. Без цветна лента и без бадж —
// състоянието е дума в изречението, не блок.

import { CloseOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";

import { formatMinutes } from "../../../../components/dataTable";
import type { BillHistory } from "../../../../api/reportsBillsHistory";
import type { BillOperations } from "../../../../api/billOperations";
import { formatShortDate } from "./billOperationsFormat";

type Props = {
  /** Липсва, докато операциите се зареждат — тогава водим по реда от справката. */
  data: BillOperations | undefined;
  bill: BillHistory;
  isNarrow: boolean;
  onClose: () => void;
};

export const BillOperationsHeader = ({
  data,
  bill,
  isNarrow,
  onClose,
}: Props) => {
  const { t } = useTranslation();

  const isClosed = (data?.bill?.status ?? bill.status) === "closed";
  const durationMinutes = data?.bill?.duration_minutes ?? bill.duration_minutes;
  const openedAt = data?.bill?.created_at ?? bill.created_at;
  const waiterName = data?.bill?.waiter?.names ?? null;
  const receiptNumber = (bill.receipt_number || "").trim();

  const status = t(
    isClosed ? "reportsPage.billOpsStatusClosed" : "reportsPage.billOpsStatusOpen",
  );
  const duration =
    durationMinutes == null
      ? null
      : formatMinutes(durationMinutes, {
          hour: t("dataTable.hourShort"),
          minute: t("dataTable.minuteShort"),
        });

  const kicker = [
    `${t("reportsPage.billOpsBillKicker")} #${bill.id}`,
    formatShortDate(openedAt),
    receiptNumber ? `${t("reportsPage.receiptNumber")} ${receiptNumber}` : null,
  ]
    .filter(Boolean)
    .join(" · ");

  // Възстановена сметка: същото id, отворена наново — казва се в същото
  // изречение, не с бадж (виж бележката горе).
  const restoresCount = data?.bill?.restores_count ?? bill.restores_count ?? 0;
  const restored =
    restoresCount > 0
      ? t("reportsPage.billOpsRestoredTimes", { count: restoresCount })
      : null;

  const subline = [
    waiterName,
    duration ? `${status}, ${duration}` : status,
    restored,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <header
      className={`flex shrink-0 items-start gap-3 border-b border-shade-1 ${
        isNarrow ? "px-4 py-4" : "px-6 py-5"
      }`}
    >
      <div className="min-w-0 flex-1">
        <p className="font-pos-mono text-[10px] font-bold uppercase leading-none tracking-[0.14em] text-muted">
          {kicker}
        </p>
        {/* leading-[1.2] — кирилските descender-и се режат при по-стегнат ред. */}
        <h2
          className={`mt-2 truncate font-extrabold leading-[1.2] tracking-[-0.02em] text-ink ${
            isNarrow ? "text-[21px]" : "text-[26px]"
          }`}
        >
          {bill.name}
        </h2>
        <p className="mt-1 text-[13px] font-medium leading-[1.4] text-soft">
          {subline}
        </p>
      </div>

      <button
        type="button"
        onClick={onClose}
        aria-label={t("reportsPage.billOpsClose")}
        className="grid size-[30px] shrink-0 place-items-center rounded-md text-muted hover:bg-shade-1 hover:text-ink"
      >
        <CloseOutlined className="text-[14px]" />
      </button>
    </header>
  );
};
